import React, { useEffect, useState } from "react";
import GenericTable from "../GenericTable/GenericTable";
import { useNavigate } from "react-router-dom";
import {
  CircularProgress,
  Button,
  Alert,
  Box,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  Fade,
  Card
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import config from "../../../config";

const syncColumns = [
  {
    id: "store_name",
    label: "Store Name",
    render: (row) => row.store_name ?? "N/A"
  },
  {
    id: "pos_name",
    label: "POS System",
    render: (row) => row.pos_name ?? "N/A"
  },
  {
    id: "last_sync_date",
    label: "Last Sync Date",
    render: (row) => row.last_sync_date ?? "N/A"
  },
  {
    id: "sync_status",
    label: "Status",
    render: (row) => row.sync_status ?? "N/A"
  },
  {
    id: "sync_button",
    label: "Sync"
  }
];



function SyncList() {
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [storeList, setStoreList] = useState([]);
  const [error, setError] = useState(null);
  const [syncLoading, setSyncLoading] = useState({});
  const [syncMessage, setSyncMessage] = useState(null);
  const [anySyncInProgress, setAnySyncInProgress] = useState(false); // Global sync lock
  const [currentSyncingStore, setCurrentSyncingStore] = useState(null); // Track which store is syncing
  const [inProgressSync, setInProgressSync] = useState(null); // Track ANY in-progress sync for this client

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedStoreForEdit, setSelectedStoreForEdit] = useState(null);

  // Manual Upload State
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedStoreForUpload, setSelectedStoreForUpload] = useState(null);
  const [manualFile, setManualFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const handleOpenUploadModal = (store) => {
    setSelectedStoreForUpload(store);
    setManualFile(null);
    setUploadError(null);
    setUploadSuccess(null);
    setIsDragging(false);
    setUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
    setSelectedStoreForUpload(null);
  };

  // --- Drag & Drop Handlers ---
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
    e.target.value = ""; // reset
  };
  const processFile = (file) => {
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls') && !fileName.endsWith('.csv')) {
      setUploadError("Invalid file type. Please upload .xlsx, .xls, or .csv");
      setManualFile(null);
      return;
    }
    setManualFile(file);
    setUploadError(null);
  };

  const handleManualUpload = async () => {
    if (!manualFile || !selectedStoreForUpload) return;

    setUploadLoading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const formDataPayload = new FormData();
      formDataPayload.append("file", manualFile);

      const headers = config.getHeaders();
      delete headers['Content-Type'];

      const isShopify = selectedStoreForUpload.platform === "Shopify";

      if (isShopify) {
        // ── Shopify: existing hybrid upload flow — untouched ─────────────────────
        let cleanUrl = selectedStoreForUpload.pos_url || "";
        cleanUrl = cleanUrl.replace("https://", "").replace("http://", "").split("/")[0];
        const uploadUrl = `${config.API_BASE_URL}/client/pos/upload-hybrid?store_url=${cleanUrl}&client_store_id=${selectedStoreForUpload.client_store_id}`;
        const response = await fetch(uploadUrl, { method: "POST", headers, body: formDataPayload });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.message || "Upload failed");
        setUploadSuccess(`Successfully updated!`);
      } else {
        // ── Non-Shopify: new universal update flow — uses saved mappings from DB ──
        const uploadUrl = `${config.API_BASE_URL}/client/pos/update-universal?client_store_id=${selectedStoreForUpload.client_store_id}`;
        const response = await fetch(uploadUrl, { method: "POST", headers, body: formDataPayload });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.message || "Upload failed");
        const { inserted = 0, updated = 0 } = result.data || {};
        setUploadSuccess(`Successfully updated! Inserted ${inserted}, Updated ${updated} products.`);
      }

      setTimeout(() => {
        handleCloseUploadModal();
        fetchConfiguredStores();
        refreshNotifications();
      }, 2000);

    } catch (err) {
      console.error("Upload Error:", err);
      setUploadError(err.message);
    } finally {
      setUploadLoading(false);
    }
  };

  // ✅ Function to refresh notifications after successful sync
  const refreshNotifications = async () => {
    try {
      console.log("🔔 Refreshing notifications after successful sync...");
      const clientId = config.clientId;
      const apiKey = config.apiKey;

      if (!clientId || !apiKey) {
        console.warn("⚠️ Cannot refresh notifications: Missing clientId or apiKey");
        return;
      }

      // Trigger a custom event that the TopNavbar Notifications component can listen to
      window.dispatchEvent(new CustomEvent('refreshNotifications'));

      console.log("✅ Notification refresh event dispatched");
    } catch (error) {
      console.error("❌ Error refreshing notifications:", error);
    }
  };

  // Custom Sync Button Component
  const SyncButton = ({ store }) => {
    const isLoading = syncLoading[store.client_store_id];
    const isThisStoreSyncing = currentSyncingStore === store.client_store_id;
    const isAnotherStoreSyncing = anySyncInProgress && !isThisStoreSyncing;

    // Manual Store Logic: Show "Update Inventory" instead of Sync
    if (store.is_manual) {
      return (
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => handleOpenUploadModal(store)}
          disabled={isAnotherStoreSyncing && !isThisStoreSyncing} // Disable if sync is running? Maybe not needed for manual upload, but keep consistency
          startIcon={<i className="material-symbols-outlined" style={{ fontSize: 16 }}>upload_file</i>}
          sx={{
            borderRadius: '20px',
            textTransform: 'none',
            fontWeight: 'bold',
            fontSize: '0.75rem',
            minWidth: '130px'
          }}
        >
          Update Data
        </Button>
      );
    }

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} color="primary" />
          <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
            Syncing...
          </Typography>
        </Box>
      );
    }

    return (
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={() => handleSyncProducts(store)}
        disabled={isAnotherStoreSyncing} // Disable if another store is syncing
        startIcon={<i className="material-symbols-outlined" style={{ fontSize: 16 }}>sync</i>}
        sx={{
          minWidth: '100px',
          borderRadius: '20px',
          textTransform: 'none',
          fontWeight: 'bold',
          fontSize: '0.75rem'
        }}
      >
      </Button>
    );
  };

  // Fetch configured stores function (moved outside useEffect so it can be reused)
  const fetchConfiguredStores = async () => {
    try {
      setLoading(true);
      const clientId = config.clientId;

      const response = await fetch(`${config.API_BASE_URL}/client/pos/configured-stores?client_id=${clientId}`, {
        method: "GET",
        headers: config.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch configured stores: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Add actions to each store (sync button will be rendered dynamically)
        const storesWithActions = (result.data || []).map(store => {
          // Format last_sync_date if it exists
          let formattedSyncDate = "N/A";

          if (store.last_sync_date) {
            const d = new Date(store.last_sync_date);
            if (!isNaN(d.getTime())) {
              const month = String(d.getMonth() + 1).padStart(2, "0");
              const day = String(d.getDate()).padStart(2, "0");
              const year = d.getFullYear();

              const dateStr = `${month}-${day}-${year}`;

              const timeStr = d.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false
              });

              formattedSyncDate = `${dateStr} ${timeStr}`;
            }
          }

          return {
            ...store,
            last_sync_date: formattedSyncDate,
            actions: store.client_store_id, // Store ID for actions
            sync_button: store.client_store_id // Store ID for sync button
          };
        });
        setStoreList(storesWithActions);

        // Check if there's a pending sync from POSConfigure
        const pendingSyncData = localStorage.getItem("pendingSync");
        if (pendingSyncData) {
          try {
            const { storeId, shouldSync, timestamp } = JSON.parse(pendingSyncData);

            // Check if sync was triggered within last 5 minutes (to avoid stale data)
            const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
            if (timestamp > fiveMinutesAgo && shouldSync) {
              console.log("🔄 Detected pending sync request for store:", storeId);

              // Find the store in the list
              const storeToSync = storesWithActions.find(s => s.client_store_id === storeId);
              if (storeToSync) {
                // Set global sync lock
                setAnySyncInProgress(true);
                setCurrentSyncingStore(storeId);

                // Show loading state immediately
                setSyncLoading(prev => ({ ...prev, [storeId]: true }));
                setSyncMessage({
                  type: 'info',
                  text: `🔄 Starting sync for ${storeToSync.store_name}... Please wait.`
                });

                // Call batch sync API and start polling
                triggerBatchSync(storeId, storeToSync.store_name);
              } else {
                console.warn("⚠️ Store not found in list:", storeId);
                localStorage.removeItem("pendingSync");
              }
            } else {
              // Stale data, clear it
              console.log("🗑️ Clearing stale pending sync data");
              localStorage.removeItem("pendingSync");
            }
          } catch (e) {
            console.error("Error parsing pending sync data:", e);
            localStorage.removeItem("pendingSync");
          }
        }

        // Also check if there's an active sync in progress (in case of page refresh)
        const activeSyncData = localStorage.getItem("activeSyncStore");
        if (activeSyncData) {
          try {
            const { storeId, storeName, timestamp } = JSON.parse(activeSyncData);

            // Check if sync was triggered within last 10 minutes
            const tenMinutesAgo = Date.now() - (10 * 60 * 1000);
            if (timestamp > tenMinutesAgo) {
              console.log("🔄 Detected active sync in progress for store:", storeName);

              // Set global sync lock
              setAnySyncInProgress(true);
              setCurrentSyncingStore(storeId);

              // Show loading state
              setSyncLoading(prev => ({ ...prev, [storeId]: true }));
              setSyncMessage({
                type: 'info',
                text: `🔄 Sync in progress for ${storeName}... Please wait.`
              });
            } else {
              // Stale data, clear it
              console.log("🗑️ Clearing stale active sync data");
              localStorage.removeItem("activeSyncStore");
            }
          } catch (e) {
            console.error("Error parsing active sync data:", e);
            localStorage.removeItem("activeSyncStore");
          }
        }
      } else {
        throw new Error(result.message || "Failed to fetch stores");
      }

    } catch (err) {
      console.error("Failed to fetch configured stores:", err);
      setError(err.message);
      setStoreList([]);
    } finally {
      setLoading(false);
    }
  };

  const checkInProgressSync = async () => {
    try {
      const clientId = config.clientId;
      const response = await fetch(`${config.API_BASE_URL}/client/sync-logs/list?client_id=${clientId}&limit=10`, {
        method: "GET",
        headers: config.getHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Find the most recent started/running sync
          const active = result.data.find(log => log.status === 'started' || log.status === 'running');
          setInProgressSync(active || null);
        }
      }
    } catch (err) {
      console.error("Failed to check in-progress sync:", err);
    }
  };

  useEffect(() => {
    fetchConfiguredStores();
    checkInProgressSync();

    // Poll for in-progress syncs every 10 seconds so the alert can disappear automatically
    const interval = setInterval(checkInProgressSync, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger batch sync API and start polling
  const triggerBatchSync = async (storeId, storeName) => {
    try {
      console.log("🚀 Calling batch sync API for store:", storeId);

      // Call batch sync API with query parameters
      const url = new URL(`${config.API_BASE_URL}/batch/sync/store`);
      url.searchParams.append('client_store_id', storeId);
      url.searchParams.append('batch_type', 'both');

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: config.getHeaders()
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Failed to start sync");
      }

      console.log("✅ Batch sync API called successfully. Batch ID:", result.batch_run_id);

      // Update message
      setSyncMessage({
        type: 'info',
        text: `🔄 Syncing ${storeName}... This may take a few minutes.`
      });

      // Start polling for status
      if (result.batch_run_id) {
        pollBatchStatus(result.batch_run_id, storeId, storeName);
      } else {
        throw new Error("No batch_run_id returned from API");
      }

    } catch (error) {
      console.error("❌ Failed to trigger batch sync:", error);
      setSyncLoading(prev => ({ ...prev, [storeId]: false }));
      setSyncMessage({
        type: 'error',
        text: `❌ Failed to start sync for ${storeName}: ${error.message}`
      });
      // Release global sync lock
      setAnySyncInProgress(false);
      setCurrentSyncingStore(null);
      // Clear localStorage on error
      localStorage.removeItem("pendingSync");
      localStorage.removeItem("activeSyncStore");
    }
  };

  // Poll batch status until completion
  const pollBatchStatus = async (batchRunId, storeId, storeName) => {
    const maxAttempts = 60; // Poll for max 5 minutes (60 * 5 seconds)
    let attempts = 0;

    const poll = async () => {
      try {
        attempts++;
        console.log(`📊 Polling batch status (attempt ${attempts}/${maxAttempts})... Batch ID: ${batchRunId}`);

        const response = await fetch(`${config.API_BASE_URL}/batch/status/${batchRunId}`, {
          method: "GET",
          headers: config.getHeaders()
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch batch status: ${response.status}`);
        }

        const result = await response.json();
        console.log("📊 Batch status response:", result);

        if (result.success && result.data) {
          const status = result.data.status;
          console.log(`📊 Current status: ${status}`);

          if (status === 'finished') {
            // Sync completed successfully
            console.log("✅ Batch sync completed!");
            setSyncLoading(prev => ({ ...prev, [storeId]: false }));
            setSyncMessage({
              type: 'success',
              text: `✅ Sync completed for ${storeName}!`
            });
            // Release global sync lock
            setAnySyncInProgress(false);
            setCurrentSyncingStore(null);
            // Clear both localStorage keys
            localStorage.removeItem("pendingSync");
            localStorage.removeItem("activeSyncStore");
            // Refresh store list to show updated last sync time
            fetchConfiguredStores();

            // ✅ Refresh notifications immediately after successful sync
            refreshNotifications();

            return; // Stop polling
          } else if (status === 'failed') {
            // Sync failed
            console.error("❌ Batch sync failed!");
            setSyncLoading(prev => ({ ...prev, [storeId]: false }));
            setSyncMessage({
              type: 'error',
              text: `❌ Sync failed for ${storeName}. Please try again.`
            });
            // Release global sync lock
            setAnySyncInProgress(false);
            setCurrentSyncingStore(null);
            // Clear both localStorage keys
            localStorage.removeItem("pendingSync");
            localStorage.removeItem("activeSyncStore");
            return; // Stop polling
          } else if (status === 'running' || status === 'pending') {
            // Still running, continue polling
            console.log(`⏳ Status is ${status}, continuing to poll...`);
            if (attempts < maxAttempts) {
              setTimeout(poll, 5000); // Poll every 5 seconds
            } else {
              // Max attempts reached
              console.warn("⚠️ Max polling attempts reached");
              setSyncLoading(prev => ({ ...prev, [storeId]: false }));
              setSyncMessage({
                type: 'warning',
                text: `⚠️ Sync is taking longer than expected for ${storeName}. Please check back later.`
              });
              // Release global sync lock
              setAnySyncInProgress(false);
              setCurrentSyncingStore(null);
              // Clear both localStorage keys
              localStorage.removeItem("pendingSync");
              localStorage.removeItem("activeSyncStore");
            }
          }
        } else {
          throw new Error("Invalid batch status response");
        }
      } catch (error) {
        console.error("❌ Error polling batch status:", error);
        setSyncLoading(prev => ({ ...prev, [storeId]: false }));
        setSyncMessage({
          type: 'error',
          text: `❌ Failed to check sync status for ${storeName}.`
        });
        // Release global sync lock
        setAnySyncInProgress(false);
        setCurrentSyncingStore(null);
        // Clear both localStorage keys on error
        localStorage.removeItem("pendingSync");
        localStorage.removeItem("activeSyncStore");
      }
    };

    // Start polling immediately
    poll();
  };

  const handleSyncProducts = async (store) => {
    const storeId = store.client_store_id;
    const storeName = store.store_name;

    // Check if any sync is already in progress
    if (anySyncInProgress) {
      console.warn("⚠️ Another sync is already in progress. Please wait.");
      setSyncMessage({
        type: 'warning',
        text: `⚠️ Another store is currently syncing. Please wait for it to complete.`
      });
      return; // Don't start a new sync
    }

    console.log("🚀 User clicked sync for store:", storeName);

    // Set global sync lock
    setAnySyncInProgress(true);
    setCurrentSyncingStore(storeId);

    // Store sync info in localStorage before calling API
    localStorage.setItem("activeSyncStore", JSON.stringify({
      storeId: storeId,
      storeName: storeName,
      timestamp: Date.now()
    }));

    // Show loading state immediately
    setSyncLoading(prev => ({ ...prev, [storeId]: true }));
    setSyncMessage({
      type: 'info',
      text: `🔄 Starting sync for ${storeName}... Please wait.`
    });

    // Call batch sync API and start polling
    triggerBatchSync(storeId, storeName);
  };



  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        Error loading stores: {error}
      </div>
    );
  }

  return (
    <>
      {syncMessage && (
        <Alert
          severity={syncMessage.type}
          sx={{ mb: 2 }}
          onClose={() => setSyncMessage(null)}
        >
          {syncMessage.text}
        </Alert>
      )}

      {inProgressSync && !syncMessage && (
        <Alert
          severity="info"
          sx={{ mb: 2, borderRadius: 2, fontWeight: 500 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/sync-logs")}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Check Status
            </Button>
          }
        >
          Sync for <strong>{inProgressSync.store_name}</strong> is in progress. You can check the detailed status at the{" "}
          <span
            onClick={() => navigate("/sync-logs")}
            style={{ textDecoration: "underline", cursor: "pointer", fontWeight: 700 }}
          >
            Sync Logs
          </span>{" "}
          page.
        </Alert>
      )}

      <GenericTable
        title="POS Management"
        columns={syncColumns}
        rows={storeList.map(store => ({
          ...store,
          sync_button: <SyncButton store={store} /> // Render sync button dynamically
        }))}
        showView={false}
        showEdit={true}
        showDelete={false}
        onEdit={(row) => {
          navigate(`/pos-sync/${row.client_store_id}/edit`);
        }}

        customHeaderButtons={[
          {
            label: "Configure New POS",
            icon: "settings",
            onClick: () => {
              navigate("/pos-sync/configure");
            },
          },
        ]}
      />

      {/* UPDATE MODAL (MOCKUP OF PREMIUM UI) */}
      <Dialog
        open={uploadModalOpen}
        onClose={handleCloseUploadModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, p: 1, boxShadow: "0 10px 40px -10px rgba(0,0,0,0.2)" }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 800 }}>Update Store Data</DialogTitle>
        <DialogContent>
          <Fade in={!!uploadError}><Box>{uploadError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{uploadError}</Alert>}</Box></Fade>
          <Fade in={!!uploadSuccess}><Box>{uploadSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{uploadSuccess}</Alert>}</Box></Fade>

          {uploadLoading && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              <strong>Sync In Progress:</strong> We are processing your file and updating products. This may take a few minutes.
            </Alert>
          )}

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Upload the latest product Excel file for <strong>{selectedStoreForUpload?.store_name}</strong> to synchronize inventory and catalog updates.
          </Typography>

          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: '2px dashed',
              borderColor: isDragging ? 'primary.main' : manualFile ? 'success.main' : 'grey.300',
              bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.05) : manualFile ? alpha(theme.palette.success.main, 0.02) : 'grey.50',
              borderRadius: 4,
              p: 5,
              textAlign: 'center',
              transition: 'all 0.2s ease-in-out',
              cursor: 'pointer',
              '&:hover': {
                borderColor: manualFile ? 'success.dark' : 'primary.main',
                bgcolor: manualFile ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.primary.main, 0.02)
              }
            }}
            onClick={() => document.getElementById("modal-file-upload").click()}
          >
            <input
              type="file"
              id="modal-file-upload"
              style={{ display: 'none' }}
              accept=".xlsx, .xls, .csv"
              onChange={handleFileSelect}
            />

            {manualFile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <CheckCircleOutlineIcon color="success" sx={{ fontSize: 50 }} />
                <Box>
                  <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>{manualFile.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{(manualFile.size / 1024).toFixed(1)} KB</Typography>
                </Box>
                <Typography variant="body2" color="primary" sx={{ mt: 0.5, fontWeight: 500, textDecoration: 'underline' }}>
                  Click or drag to replace
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <CloudUploadIcon color={isDragging ? "primary" : "action"} sx={{ fontSize: 50, opacity: 0.8 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Drag & Drop your Export
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to browse files (.xlsx, .csv)
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseUploadModal} disabled={uploadLoading} sx={{ borderRadius: 2, fontWeight: 600 }}>Cancel</Button>
          <Button
            onClick={handleManualUpload}
            variant="contained"
            disabled={!manualFile || uploadLoading}
            startIcon={uploadLoading ? <CircularProgress size={20} color="inherit" /> : <AssignmentTurnedInIcon />}
            sx={{ px: 4, py: 1, borderRadius: 2, fontWeight: 700 }}
          >
            {uploadLoading ? "Processing Sync..." : "Confirm & Sync Data"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DYNAMIC EDIT SELECTION MODAL */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, p: 2, boxShadow: "0 10px 40px -10px rgba(0,0,0,0.2)" }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 800, textAlign: "center" }}>
          Edit Configuration
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            What would you like to update for <strong>{selectedStoreForEdit?.store_name}</strong>?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {selectedStoreForEdit?.pos_name?.toLowerCase().includes("wordpress") ||
            selectedStoreForEdit?.pos_name?.toLowerCase().includes("woocommerce") ? (
            <>
              {/* WordPress Options */}
              <Card
                variant="outlined"
                sx={{ p: 2, cursor: "pointer", "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.05) } }}
                onClick={() => {
                  setEditModalOpen(false);
                  navigate(`/pos-sync/${selectedStoreForEdit.client_store_id}/edit`);
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">Update API Credentials</Typography>
                <Typography variant="body2" color="text.secondary">Update your Consumer Key and Secret to restore connection.</Typography>
              </Card>

              <Card
                variant="outlined"
                sx={{ p: 2, cursor: "pointer", "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.05) } }}
                onClick={() => {
                  setEditModalOpen(false);
                  navigate(`/pos-sync/${selectedStoreForEdit.client_store_id}/edit`);
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">Edit Chatbot Filters</Typography>
                <Typography variant="body2" color="text.secondary">Configure Categories, Moods, SVGs, and Potency logic for the chatbot.</Typography>
              </Card>

              <Card
                variant="outlined"
                sx={{ p: 2, cursor: "pointer", "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.05) } }}
                onClick={() => {
                  setEditModalOpen(false);
                  navigate(`/pos-sync/${selectedStoreForEdit.client_store_id}/reanalyze-wordpress`);
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">Re-run WordPress Analysis</Typography>
                <Typography variant="body2" color="text.secondary">Added potency or mood metadata to your site recently? Re-run our AI analysis to detect them!</Typography>
              </Card>
            </>
          ) : (
            <>
              {/* Shopify / Universal Options */}
              <Card
                variant="outlined"
                sx={{ p: 2, cursor: "pointer", "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.05) } }}
                onClick={() => {
                  setEditModalOpen(false);
                  handleOpenUploadModal(selectedStoreForEdit);
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">Upload New Data File</Typography>
                <Typography variant="body2" color="text.secondary">Upload a new Excel file to introduce new products, categories, or moods.</Typography>
              </Card>

              <Card
                variant="outlined"
                sx={{ p: 2, cursor: "pointer", "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.05) } }}
                onClick={() => {
                  setEditModalOpen(false);
                  navigate(`/pos-sync/${selectedStoreForEdit.client_store_id}/edit`);
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">Edit Field Mappings</Typography>
                <Typography variant="body2" color="text.secondary">Change how your Excel columns map to the database fields.</Typography>
              </Card>

              <Card
                variant="outlined"
                sx={{ p: 2, cursor: "pointer", "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.05) } }}
                onClick={() => {
                  setEditModalOpen(false);
                  navigate(`/pos-sync/${selectedStoreForEdit.client_store_id}/edit-filters`);
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">Edit Chatbot Filters</Typography>
                <Typography variant="body2" color="text.secondary">Configure Categories, Moods, SVGs, and Potency logic for the chatbot.</Typography>
              </Card>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setEditModalOpen(false)} color="inherit">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SyncList;
